import React, { useEffect, useState } from 'react';
import { Col, ComponentLoader, HorizontalRule, InPageAlert, Row, SectionPage } from '@snsw/react-component-library';
import { useDispatch, useSelector } from 'react-redux';
import { PagePath } from 'shared/constants/pagePath';
import { useNavigate } from 'react-router-dom';
import ScrollToTop from 'shared/utils/scrollToTop';
import { FeeSummary, NavigationButtons } from '@onegov/react-component-library';
import { LoadingStates } from 'shared/constants/loadingStates';
import { getPlantItemRenewalFees } from 'plantItemRenewal/redux/plantItemRenewalFeesSlice';
import { initPlantItemRenewalPayment } from 'plantItemRenewal/redux/plantItemRenewalPaymentSlice';
import { store } from 'shared/redux/configureStore';
import { getPlantItemRenewallLicenceTypeCode } from 'plantItemRenewal/utils/PlantItemRenewalHelpers';
import { createApplication } from 'shared/redux/applicationSlice';

export const PlantItemRenewalFees = (props) => {
  ScrollToTop();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fees = useSelector((state) => state.plantItemRenewalFees);
  const payment = useSelector((state) => state.plantItemRenewalPayment);
  const application = useSelector((state) => state.application);
  const plantItemLicenceTypes = useSelector((state) => state.plantItemLicenceTypes);
  const { paymentReference } = payment;
  const [isLoading, setIsLoading] = useState(false);

  const formatter = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  });

  const paymentsReferenceMessage = !paymentReference
    ? 'If you continue, you will no longer be able to edit your application.'
    : '';

  useEffect(() => {
    dispatch(getPlantItemRenewalFees());
  }, [dispatch]);

  const onContinueClick = () => {
    if (fees.status === LoadingStates.Succeeded) {
      if (!application.createdApplication) {
        // if application has not been created, create new application
        const plantItemRenewalLicenceCode = getPlantItemRenewallLicenceTypeCode(plantItemLicenceTypes);
        store
          .dispatch(createApplication(plantItemRenewalLicenceCode))
          .unwrap()
          .then(() => {
            initPayment();
          });
      } else {
        initPayment();
      }
    }
  };

  const initPayment = () => {
    store
      .dispatch(initPlantItemRenewalPayment())
      .unwrap()
      .then((data) => {
        setIsLoading(true);
        window.location.href = `${data.paymentRedirectUrl}${data.paymentReference}`;
      });
  };

  const onBackClick = () => {
    navigate(PagePath.plantItemRenewalDeclaration);
  };

  const mapFeeComponents = (data = {}) => {
    const feeComponents = [
      {
        feeName: `First Item (${formatter.format(data.baseFee)})`,
        value: data.baseFee,
        quantity: 1,
      },
    ];

    if (data.additionalItemsCount) {
      const feePerItem = formatter.format(data.feePerAdditionalItem);

      feeComponents.push({
        feeName: `Additional items (${feePerItem} each)`,
        value: data.feePerAdditionalItem * data.additionalItemsCount,
        quantity: data.additionalItemsCount,
      });
    }

    return feeComponents;
  };

  return (
    <SectionPage>
      <Row>
        <Col span={9}>
          {!paymentReference ? (
            <InPageAlert
              variant="info"
              title="You’ll be taken to a secure platform provided by Service NSW to complete your payment and submit your application."
              headingLevel="h4"
            >
              <p>Please call SafeWork NSW on 13 10 50 if you need help to process this renewal.</p>
            </InPageAlert>
          ) : (
            <InPageAlert
              variant="warning"
              title="Your application has been saved and cannot be edited"
              headingLevel="h4"
            >
              <p>Continue to payment to finalise your application. For help, call SafeWork on 13 10 50.</p>
            </InPageAlert>
          )}

          <div style={{ marginTop: 32, marginBottom: 64 }}>
            <FeeSummary
              isLoading={fees.status === 'loading'}
              title="Fee breakdown"
              feeComponents={fees?.data && mapFeeComponents(fees.data)}
            />
          </div>
        </Col>
      </Row>

      <HorizontalRule />
      <p>{paymentsReferenceMessage}</p>
      <NavigationButtons
        leftButtonConfig={{
          label: 'Continue',
          onClick: onContinueClick,
        }}
        rightButtonConfig={{
          isVisible: false,
        }}
        backButtonConfig={{
          onClick: onBackClick,
          isVisible: !paymentReference,
        }}
      />

      {(payment.status === LoadingStates.Loading || isLoading || application.creatingApplication) && (
        <ComponentLoader fullPage />
      )}
    </SectionPage>
  );
};
